import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import { FaTrash, FaTrashAlt, FaEye, FaUser } from 'react-icons/fa';
import generateCaptcha from '../../components/captcha';
import formatDateTime from '../../components/formatDateTime';
import NotFound from '../../components/ul/NotFound';
import Navbar from './Navbar';

const UserContact = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [contactData, setContactData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    useEffect(() => {
        // Reset select all when page changes
        setSelectAll(false);
        setSelectedItems([]);
    }, [currentPage]);

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getContactUser();
            if (resp?.status) {
                setContactData(resp.data || []);
                setTotalPages(resp.totalPages || 1);
            } else {
                setError(resp?.message || 'Failed to fetch Help & Support data');
            }
        } catch (error) {
            setError('An error occurred while fetching data');
            console.error(error);
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async (id) => {
        const content = `You are about to delete a Help & Support submission. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        setLoader(true);
        try {
            const resp = await Calling.deleteContactUser({ id: [id] });
            if (resp?.status) {
                setSuccess('Help & Support submission deleted successfully');
                toast.success('Help & Support submission deleted successfully');
                fetchData();
                setSelectedItems(selectedItems.filter(item => item !== id));
            } else {
                setError(resp?.message || 'Failed to delete Help & Support submission');
                toast.error(resp?.message || 'Failed to delete Help & Support submission');
            }
        } catch (error) {
            setError('An error occurred while deleting data');
            toast.error('An error occurred while deleting data');
            console.error(error);
        } finally {
            setLoader(false);
        }
    };

    const handleMultipleDelete = async () => {
        if (selectedItems.length === 0) {
            toast.error('Please select items to delete', { autoClose: 2000 });
            return;
        }

        const content = `You are about to delete ${selectedItems.length} Help & Support submission(s). To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (!check) return toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        setLoader(true);
        try {
            const resp = await Calling.deleteContactUser({ id: selectedItems });
            if (resp?.status) {
                setSuccess(`${selectedItems.length} Help & Support submission(s) deleted successfully`);
                toast.success(`${selectedItems.length} Help & Support submission(s) deleted successfully`);
                fetchData();
                setSelectedItems([]);
                setSelectAll(false);
            } else {
                setError(resp?.message || 'Failed to delete Help & Support submissions');
                toast.error(resp?.message || 'Failed to delete Help & Support submissions');
            }
        } catch (error) {
            setError('An error occurred while deleting data');
            toast.error('An error occurred while deleting data');
            console.error(error);
        } finally {
            setLoader(false);
        }
    };

    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            const allIds = contactData.map(contact => contact._id);
            setSelectedItems(allIds);
        }
        setSelectAll(!selectAll);
    };

    const handleViewUser = (userId) => {
        if (userId) {
            navigate(`/manage/user/all/info/${userId}`);
        } else {
            toast.error('User information not available', { autoClose: 2000 });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 mb-2">
                <h1 className="text-xl md:text-2xl font-bold text-main-font w-auto">Help & Support Submissions</h1>
                {selectedItems.length > 0 && (
                    <button
                        onClick={handleMultipleDelete}
                        className="mt-3 sm:mt-0 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-md"
                    >
                        <FaTrashAlt />
                        Delete Selected ({selectedItems.length})
                    </button>
                )}
            </div>

            <div className='bg-white rounded-2xl shadow-lg overflow-hidden'>
                <Navbar selectState={'message'} />
                <hr className='border-gray-200' />

                {error && (
                    <div className="m-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
                        <p className="font-medium">Error</p>
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="m-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md">
                        <p className="font-medium">Success</p>
                        <p className="text-sm">{success}</p>
                    </div>
                )}

                {loader ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader />
                    </div>
                ) : contactData.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="py-4 px-4 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </th>
                                        <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">User</th>
                                        <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Help & Support Option</th>
                                        <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Message</th>
                                        <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                        <th className="py-4 px-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {contactData.map((contact) => (
                                        <tr key={contact._id} className="hover:bg-gray-50 transition-colors duration-150">
                                            <td className="py-4 px-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.includes(contact._id)}
                                                    onChange={() => handleSelectItem(contact._id)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            </td>
                                            <td className="py-4 px-4">
                                                {contact.userId ? (
                                                    <div className="flex items-center space-x-3">
                                                        <div className="flex-shrink-0">
                                                            {contact.userId.u_profile ? (
                                                                <img
                                                                    src={contact.userId.u_profile}
                                                                    alt={contact.userId.u_name}
                                                                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHBhdGggZD0iTTIwIDE4QzIyLjIwOTEgMTggMjQgMTYuMjA5MSAyNCAxNEMyNCAxMS43OTA5IDIyLjIwOTEgMTAgMjAgMTBDMTcuNzkwOSAxMCAxNiAxMS43OTA5IDE2IDE0QzE2IDE2LjIwOTEgMTcuNzkwOSAxOCAyMCAxOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPg==';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                                    <FaUser className="w-5 h-5 text-gray-500" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900">{contact.userId.u_name}</div>
                                                            <div className="text-gray-500 text-sm">{contact.userId.u_email}</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 italic">Unknown User</span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                    {contact.option}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4">
                                                <p className="text-gray-700 max-w-xs truncate" title={contact.message || '-'}>
                                                    {contact.message || '-'}
                                                </p>
                                            </td>
                                            <td className="py-4 px-4 text-gray-600 text-sm">
                                                {formatDateTime(contact.createdAt)}
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    {contact.userId && (
                                                        <button
                                                            onClick={() => handleViewUser(contact.userId._id)}
                                                            className="inline-flex items-center justify-center p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                                            title="View User"
                                                        >
                                                            <FaEye className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(contact._id)}
                                                        className="inline-flex items-center justify-center p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                                        title="Delete"
                                                    >
                                                        <FaTrash className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex justify-center items-center py-6 px-4 border-t border-gray-200">
                            <nav className="flex items-center space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex items-center space-x-1">
                                    {[...Array(totalPages).keys()].map((page) => {
                                        const pageNum = page + 1;
                                        // Show first page, last page, current page, and pages around current
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${currentPage === pageNum
                                                        ? 'bg-blue-500 text-white shadow-md'
                                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 2 ||
                                            pageNum === currentPage + 2
                                        ) {
                                            return (
                                                <span key={pageNum} className="px-2 text-gray-400">
                                                    ...
                                                </span>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${currentPage === totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                        }`}
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </>
                ) : (
                    <div className="py-12">
                        <NotFound />
                    </div>
                )}
            </div>
        </div >
    );
};

export default UserContact;